import { useState, useEffect } from 'react'
import { Search, Filter, RefreshCw, Sparkles, Activity, User as UserIcon, Clock, Globe, Calendar, Shield, ChevronRight, Inbox } from 'lucide-react'
import { auditAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'

interface AuditLogEntry {
  id: number
  actor_name: string
  actor_email: string | null
  action: string
  action_display: string
  target_model: string
  target_id: string
  metadata: Record<string, any>
  ip_address: string | null
  created_at: string
}

const ACTION_STYLES: Record<string, { chip: string; dot: string; bar: string }> = {
  CHECK_IN:             { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  CHECK_OUT:            { chip: 'bg-orange-50 text-orange-700 ring-orange-200',   dot: 'bg-orange-500',  bar: 'bg-orange-500' },
  REPORT_SUBMITTED:     { chip: 'bg-blue-50 text-blue-700 ring-blue-200',         dot: 'bg-blue-500',    bar: 'bg-blue-500' },
  EMPLOYEE_CREATED:     { chip: 'bg-violet-50 text-violet-700 ring-violet-200',   dot: 'bg-violet-500',  bar: 'bg-violet-500' },
  EMPLOYEE_UPDATED:     { chip: 'bg-violet-50 text-violet-700 ring-violet-200',   dot: 'bg-violet-500',  bar: 'bg-violet-500' },
  EMPLOYEE_DEACTIVATED: { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-500', bar: 'bg-neutral-400' },
  LEAVE_APPROVED:       { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  LEAVE_REJECTED:       { chip: 'bg-red-50 text-red-700 ring-red-200',            dot: 'bg-red-500',     bar: 'bg-red-500' },
}

const DEFAULT_ACTION = { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-400', bar: 'bg-neutral-300' }

export default function AuditLogPage() {
  const { error: toastError } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)

  const load = async (pageArg = page) => {
    setIsLoading(true)
    try {
      const params: any = { page: pageArg }
      if (actionFilter) params.action = actionFilter
      if (searchTerm) params.search = searchTerm
      const res = await auditAPI.list(params)
      const parsed = parsePaginatedResponse(res.data)
      setLogs(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load audit logs.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      load(1)
    }, 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, actionFilter])

  useEffect(() => {
    load(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      CHECK_IN: 'bg-green-100 text-green-700',
      CHECK_OUT: 'bg-orange-100 text-orange-700',
      REPORT_SUBMITTED: 'bg-blue-100 text-blue-700',
      EMPLOYEE_CREATED: 'bg-purple-100 text-purple-700',
      EMPLOYEE_UPDATED: 'bg-purple-100 text-purple-700',
      EMPLOYEE_DEACTIVATED: 'bg-neutral-200 text-neutral-700',
      LEAVE_APPROVED: 'bg-green-100 text-green-700',
      LEAVE_REJECTED: 'bg-red-100 text-red-700',
    }
    return colors[action] || 'bg-neutral-100 text-neutral-700'
  }

  const hasActiveFilters = Boolean(searchTerm || actionFilter)

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Security
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Audit Log
          </h1>
          <p className="text-sm text-neutral-500">Track all system activities and user actions</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2 shadow-sm">
            <Activity className="w-4 h-4 text-[#C24A0A]" />
            <span className="text-sm font-semibold text-neutral-800 tabular-nums">{count}</span>
            <span className="text-xs text-neutral-500">{count === 1 ? 'event' : 'events'}</span>
          </div>
          <button
            onClick={() => load(page)}
            className="group flex items-center gap-2 px-4 py-2.5 border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 rounded-xl font-medium text-sm transition-all duration-300 hover:-translate-y-0.5"
          >
            <RefreshCw className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
            <Filter className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
            Filters
          </h2>
          {hasActiveFilters && (
            <button
              onClick={() => { setSearchTerm(''); setActionFilter('') }}
              className="ml-auto text-xs font-medium text-neutral-500 hover:text-[#C24A0A] transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Search</label>
            <div className="relative group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
              <input
                type="text"
                placeholder="Target model or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Action Type</label>
            <div className="relative group">
              <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
              >
                <option value="">All Actions</option>
                <option value="CHECK_IN">Check In</option>
                <option value="CHECK_OUT">Check Out</option>
                <option value="REPORT_SUBMITTED">EOD Report</option>
                <option value="EMPLOYEE_CREATED">Employee Created</option>
                <option value="EMPLOYEE_UPDATED">Employee Updated</option>
                <option value="EMPLOYEE_DEACTIVATED">Employee Deactivated</option>
                <option value="LEAVE_APPROVED">Leave Approved</option>
                <option value="LEAVE_REJECTED">Leave Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Timeline ── */}
      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <Inbox className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No audit logs found</p>
          <p className="text-xs text-neutral-400">Try adjusting your filters or search terms.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const style = ACTION_STYLES[log.action] || DEFAULT_ACTION
            const created = new Date(log.created_at)
            const metaKeys = log.metadata ? Object.keys(log.metadata) : []

            return (
              <div
                key={log.id}
                className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
              >
                {/* Left accent bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />

                <div className="p-5 pl-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Left — main content */}
                    <div className="flex-1 min-w-0">
                      {/* Top row: action pill + actor */}
                      <div className="flex items-center gap-2 flex-wrap mb-2.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${style.chip}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                          {log.action_display || log.action.replace(/_/g, ' ')}
                        </span>

                        {/* Actor chip */}
                        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                            {log.actor_name?.charAt(0).toUpperCase() || '?'}
                          </span>
                          <span className="font-medium text-neutral-700">{log.actor_name}</span>
                          {log.actor_email && (
                            <span className="hidden sm:inline text-neutral-400 truncate max-w-[180px]">
                              · {log.actor_email}
                            </span>
                          )}
                        </span>
                      </div>

                      {/* Target */}
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-neutral-900 truncate">
                          {log.target_model}
                          <span className="text-neutral-400 font-normal mx-1.5">·</span>
                          <span className="tabular-nums text-neutral-600">{log.target_id}</span>
                        </p>
                      </div>

                      {/* Metadata */}
                      {metaKeys.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(log.metadata).map(([k, v]) => (
                            <span
                              key={k}
                              className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md bg-neutral-50 border border-neutral-200 text-[11px] text-neutral-600"
                              title={`${k}: ${v}`}
                            >
                              <span className="font-semibold text-neutral-500">{k}:</span>
                              <span className="truncate max-w-[200px]">{String(v)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right — timestamp + IP */}
                    <div className="shrink-0 lg:text-right flex lg:flex-col items-center lg:items-end gap-3 lg:gap-1 text-xs">
                      <div className="flex items-center gap-1.5 lg:flex-col lg:items-end">
                        <span className="inline-flex items-center gap-1 font-semibold text-neutral-900 tabular-nums">
                          <Clock className="w-3 h-3 text-neutral-400" />
                          {created.toLocaleTimeString()}
                        </span>
                        <span className="inline-flex items-center gap-1 text-neutral-500">
                          <Calendar className="w-3 h-3 text-neutral-400" />
                          {created.toLocaleDateString()}
                        </span>
                      </div>
                      {log.ip_address && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-neutral-400 tabular-nums">
                          <Globe className="w-3 h-3" />
                          {log.ip_address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />
    </div>
  )
}

// import { useState, useEffect } from 'react'
// import { Search, Filter, RefreshCw } from 'lucide-react'
// import { auditAPI } from '../../services/api'
// import { useToast, getErrorMessage } from '../../components/Toast'

// interface AuditLogEntry {
//   id: number
//   actor_name: string
//   actor_email: string | null
//   action: string
//   action_display: string
//   target_model: string
//   target_id: string
//   metadata: Record<string, any>
//   ip_address: string | null
//   created_at: string
// }

// export default function AuditLogPage() {
//   const { error: toastError } = useToast()
//   const [searchTerm, setSearchTerm] = useState('')
//   const [actionFilter, setActionFilter] = useState('')
//   const [logs, setLogs] = useState<AuditLogEntry[]>([])
//   const [isLoading, setIsLoading] = useState(true)

//   const load = async () => {
//     setIsLoading(true)
//     try {
//       const params: any = {}
//       if (actionFilter) params.action = actionFilter
//       if (searchTerm) params.search = searchTerm
//       const res = await auditAPI.list(params)
//       setLogs(res.data.results || res.data)
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to load audit logs.'))
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     const timeout = setTimeout(load, 300)
//     return () => clearTimeout(timeout)
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [searchTerm, actionFilter])

//   const getActionColor = (action: string) => {
//     const colors: Record<string, string> = {
//       CHECK_IN: 'bg-green-100 text-green-700',
//       CHECK_OUT: 'bg-orange-100 text-orange-700',
//       REPORT_SUBMITTED: 'bg-blue-100 text-blue-700',
//       EMPLOYEE_CREATED: 'bg-purple-100 text-purple-700',
//       EMPLOYEE_UPDATED: 'bg-purple-100 text-purple-700',
//       EMPLOYEE_DEACTIVATED: 'bg-neutral-200 text-neutral-700',
//       LEAVE_APPROVED: 'bg-green-100 text-green-700',
//       LEAVE_REJECTED: 'bg-red-100 text-red-700',
//     }
//     return colors[action] || 'bg-neutral-100 text-neutral-700'
//   }

//   return (
//     <div className="p-6 md:p-8">
//       <div className="flex items-center justify-between mb-2">
//         <div>
//           <h1 className="text-3xl font-bold text-secondary mb-2">Audit Log</h1>
//           <p className="text-neutral-600">Track all system activities and user actions</p>
//         </div>
//         <button
//           onClick={load}
//           className="flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-lg text-sm font-medium hover:bg-neutral-50"
//         >
//           <RefreshCw className="w-4 h-4" /> Refresh
//         </button>
//       </div>

//       {/* Filters */}
//       <div className="mt-8 mb-6 bg-white rounded-lg shadow border border-neutral-200 p-6">
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//           <div>
//             <label className="block text-sm font-medium text-neutral-700 mb-2">Search</label>
//             <div className="relative">
//               <Search className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
//               <input
//                 type="text"
//                 placeholder="Target model or ID..."
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
//               />
//             </div>
//           </div>
//           <div>
//             <label className="block text-sm font-medium text-neutral-700 mb-2">Action Type</label>
//             <select
//               value={actionFilter}
//               onChange={(e) => setActionFilter(e.target.value)}
//               className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
//             >
//               <option value="">All Actions</option>
//               <option value="CHECK_IN">Check In</option>
//               <option value="CHECK_OUT">Check Out</option>
//               <option value="REPORT_SUBMITTED">EOD Report</option>
//               <option value="EMPLOYEE_CREATED">Employee Created</option>
//               <option value="EMPLOYEE_UPDATED">Employee Updated</option>
//               <option value="EMPLOYEE_DEACTIVATED">Employee Deactivated</option>
//               <option value="LEAVE_APPROVED">Leave Approved</option>
//               <option value="LEAVE_REJECTED">Leave Rejected</option>
//             </select>
//           </div>
//         </div>
//       </div>

//       {/* Timeline */}
//       <div className="space-y-4">
//         {isLoading ? (
//           <div className="text-center py-12 text-neutral-600">Loading audit logs...</div>
//         ) : logs.length === 0 ? (
//           <div className="text-center py-12">
//             <Filter className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
//             <p className="text-neutral-600">No audit logs found matching your criteria</p>
//           </div>
//         ) : (
//           logs.map((log) => (
//             <div
//               key={log.id}
//               className="bg-white rounded-lg shadow border border-neutral-200 p-6 hover:shadow-lg transition-shadow"
//             >
//               <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
//                 <div className="flex-1">
//                   <div className="flex items-center gap-3 mb-2 flex-wrap">
//                     <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
//                       {log.action_display || log.action.replace(/_/g, ' ')}
//                     </span>
//                     <span className="text-xs text-neutral-500">By: {log.actor_name}</span>
//                   </div>
//                   <p className="text-neutral-700 font-medium">
//                     {log.target_model} — {log.target_id}
//                   </p>
//                   {log.metadata && Object.keys(log.metadata).length > 0 && (
//                     <p className="text-sm text-neutral-500 mt-2 break-words">
//                       {Object.entries(log.metadata)
//                         .map(([k, v]) => `${k}: ${v}`)
//                         .join(' · ')}
//                     </p>
//                   )}
//                 </div>
//                 <div className="text-right shrink-0">
//                   <p className="text-sm font-semibold text-neutral-900">
//                     {new Date(log.created_at).toLocaleTimeString()}
//                   </p>
//                   <p className="text-xs text-neutral-600">{new Date(log.created_at).toLocaleDateString()}</p>
//                 </div>
//               </div>
//             </div>
//           ))
//         )}
//       </div>
//     </div>
//   )
// }
